import { useState, Fragment } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { PHASES } from "./phases";
import { Reveal } from "./Reveal";

const tint = (hex) => `${hex}1A`;

const PhaseBullets = ({ phase }) => (
    <ul className="space-y-4">
        {phase.bullets.map((b) => (
            <li key={b.lead} className="flex gap-3">
                <span
                    className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full"
                    style={{ background: phase.colour }}
                />
                <p className="font-body text-[15px] text-black/65 leading-relaxed">
                    <span className="font-semibold text-black">{b.lead}</span>{" "}
                    {b.desc}
                </p>
            </li>
        ))}
    </ul>
);

/* ---------- Desktop: horizontal stepper + detail panel ---------- */

const StepBadge = ({ phase, active }) => {
    const Icon = phase.icon;
    return (
        <span
            className="relative shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-white transition-all duration-300"
            style={{
                background: phase.colour,
                boxShadow: active ? `0 0 0 5px ${tint(phase.colour)}` : "none",
                transform: active ? "scale(1.08)" : "scale(1)",
            }}
        >
            <Icon size={22} strokeWidth={1.9} />
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black text-white font-mono text-[10px] flex items-center justify-center">
                {phase.num}
            </span>
        </span>
    );
};

const DesktopNode = ({ phase, active, onSelect }) => (
    <button
        data-testid={`phase-node-${phase.num}`}
        onClick={onSelect}
        onMouseEnter={onSelect}
        className="group flex-1 flex flex-col items-center text-center px-3 focus:outline-none"
    >
        <StepBadge phase={phase} active={active} />
        <span
            className="mt-4 font-mono text-sm font-semibold leading-snug transition-colors"
            style={{ color: active ? phase.colour : "#000000" }}
        >
            {phase.title}
        </span>
        <span className="mt-1 font-mono text-[11px] text-zinc-400">
            ({phase.tagline})
        </span>
    </button>
);

const Connector = ({ filled, colour }) => (
    <div className="flex-none w-10 flex items-center pt-[21px]">
        <span
            className="h-[2px] flex-1 rounded transition-colors duration-300"
            style={{ background: filled ? colour : "#e4e4e7" }}
        />
        <ChevronRight
            size={16}
            className="-ml-1 transition-colors duration-300"
            style={{ color: filled ? colour : "#d4d4d8" }}
        />
    </div>
);

const PhaseDetail = ({ phase }) => (
    <div
        key={phase.num}
        data-testid="phase-detail"
        className="rise-in mt-12 bg-white rounded-2xl border border-zinc-200 p-8 sm:p-10"
        style={{ borderLeft: `4px solid ${phase.colour}` }}
    >
        <div className="flex items-center gap-4">
            <span
                className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-mono text-sm text-white"
                style={{ background: phase.colour }}
            >
                {phase.num}
            </span>
            <div>
                <h3 className="font-mono text-xl font-semibold text-black leading-tight tracking-[-0.01em]">
                    {phase.title}
                </h3>
                <span
                    className="font-mono text-xs"
                    style={{ color: phase.colour }}
                >
                    ({phase.tagline})
                </span>
            </div>
        </div>
        <p className="font-body text-base text-black/60 mt-5 leading-relaxed">
            {phase.intro}
        </p>
        <div className="mt-7">
            <PhaseBullets phase={phase} />
        </div>
    </div>
);

const DesktopFlow = ({ active, setActive }) => (
    <div className="hidden lg:block">
        <div className="flex items-start">
            {PHASES.map((p, i) => (
                <Fragment key={p.num}>
                    <DesktopNode
                        phase={p}
                        active={i === active}
                        onSelect={() => setActive(i)}
                    />
                    {i < PHASES.length - 1 && (
                        <Connector filled={i < active} colour={PHASES[i].colour} />
                    )}
                </Fragment>
            ))}
        </div>
        <PhaseDetail phase={PHASES[active]} />
    </div>
);

/* ---------- Mobile: vertical stacked accordion flow ---------- */

const MobileNode = ({ phase, open, onToggle, isLast }) => {
    const Icon = phase.icon;
    return (
        <div className="flex gap-4">
            <div className="flex flex-col items-center">
                <span
                    className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white"
                    style={{ background: phase.colour }}
                >
                    <Icon size={20} strokeWidth={1.9} />
                </span>
                {!isLast && (
                    <span className="w-[2px] flex-1 my-1.5 bg-zinc-200" />
                )}
            </div>

            <div className="flex-1 min-w-0 pb-6">
                <button
                    data-testid={`phase-acc-${phase.num}`}
                    onClick={onToggle}
                    className="w-full text-left bg-white rounded-2xl border border-zinc-200 p-5"
                    style={open ? { borderLeft: `4px solid ${phase.colour}` } : {}}
                >
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <span className="font-mono text-[11px] text-zinc-400">
                                PHASE {phase.num}
                            </span>
                            <div className="font-mono text-sm font-semibold text-black leading-snug mt-0.5">
                                {phase.title}
                            </div>
                            <span
                                className="font-mono text-[11px]"
                                style={{ color: phase.colour }}
                            >
                                ({phase.tagline})
                            </span>
                        </div>
                        <ChevronDown
                            size={18}
                            className={`shrink-0 text-zinc-400 transition-transform duration-300 ${
                                open ? "rotate-180" : ""
                            }`}
                        />
                    </div>

                    {open && (
                        <div
                            data-testid={`phase-acc-panel-${phase.num}`}
                            className="reveal-field mt-4 pt-4 border-t border-zinc-100"
                        >
                            <p className="font-body text-sm text-black/60 leading-relaxed mb-4">
                                {phase.intro}
                            </p>
                            <PhaseBullets phase={phase} />
                        </div>
                    )}
                </button>
            </div>
        </div>
    );
};

const MobileFlow = ({ open, setOpen }) => (
    <div className="lg:hidden">
        {PHASES.map((p, i) => (
            <MobileNode
                key={p.num}
                phase={p}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
                isLast={i === PHASES.length - 1}
            />
        ))}
    </div>
);

export const PhaseWorkflow = () => {
    const [active, setActive] = useState(0);
    const [open, setOpen] = useState(0);

    return (
        <section data-testid="phase-workflow" className="relative py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <Reveal className="max-w-2xl mb-14">
                    <span className="font-mono text-xs tracking-[-0.01em] text-zinc-500">
                        // DELIVERY WORKFLOW
                    </span>
                    <h2 className="font-mono tracking-[-0.01em] text-3xl sm:text-4xl font-semibold text-black mt-3">
                        The 5-Phase Automation Delivery Workflow
                    </h2>
                    <p className="font-body text-base sm:text-lg text-black/60 mt-4 leading-relaxed">
                        Five sequential phases take every client from discovery
                        to go-live — mapping the groundwork, laying secure
                        tunnelling, configuring the AI, aligning with
                        regulations, then launching with a human safety net.
                        Hover or tap any phase to explore what we deliver.
                    </p>
                </Reveal>

                <Reveal delay={120}>
                    <DesktopFlow active={active} setActive={setActive} />
                    <MobileFlow open={open} setOpen={setOpen} />
                </Reveal>
            </div>
        </section>
    );
};
